import { useInfiniteQuery } from '@tanstack/react-query';
import { getBrowserClient } from '@/lib/supabase';
import { useHostessStore } from '@/stores/hostess';
import { DAY_OF_WEEK, toHostessVisibleCustomerCategoryEmoji, isKanaQuery, toKatakana } from '@sakura-grp/lib';
import { getReservationType, type Reservation } from '@/lib/api/reservations';

const PAGE_SIZE = 50;

export interface ReservationsPage {
  reservations: Reservation[];
  totalRows: number;
  nextPage: number | undefined;
}

export interface ReservationFilters {
  search: string;
  storeIds: string[] | null;
  futureOnly: boolean;
  dateFrom: string | null;
  dateTo: string | null;
  sortAscending: boolean;
}

export function useReservationsInfinite(filters: ReservationFilters) {
  const { hostess } = useHostessStore();
  const hostessId = hostess?.id;

  const searchTrimmed = filters.search.trim();
  const searchParam = searchTrimmed || undefined;
  const searchKanaParam =
    searchTrimmed.length >= 2 && isKanaQuery(searchTrimmed)
      ? toKatakana(searchTrimmed)
      : undefined;

  return useInfiniteQuery<ReservationsPage>({
    queryKey: [
      'reservations',
      hostessId,
      filters.storeIds,
      searchParam,
      searchKanaParam,
      filters.futureOnly,
      filters.dateFrom,
      filters.dateTo,
      filters.sortAscending,
    ],
    queryFn: async ({ pageParam }) => {
      const supabase = getBrowserClient();
      const { data, error } = await supabase.rpc('get_hostess_reservations', {
        p_hostess_id: hostessId!,
        p_search: searchParam ?? undefined,
        p_search_kana: searchKanaParam,
        p_store_ids: filters.storeIds ?? undefined,
        p_future_only: filters.futureOnly,
        p_date_from: filters.dateFrom ?? undefined,
        p_date_to: filters.dateTo ?? undefined,
        p_sort_ascending: filters.sortAscending,
        p_page: pageParam as number,
        p_page_size: PAGE_SIZE,
      });

      if (error) throw new Error(`予約取得エラー: ${error.message}`);

      const rows = data || [];
      const totalRows = rows[0]?.total_rows ?? 0;
      const now = new Date();

      const reservations: Reservation[] = rows.map((row: any) => {
        const bookingDate = new Date(row.booking_date);
        const isPast = bookingDate < now;
        const type = getReservationType(row.booking_type);

        return {
          id: row.id,
          store: row.store_name || '不明',
          type,
          date: bookingDate
            .toLocaleDateString('ja-JP', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
            })
            .replace(/\//g, '.'),
          time: bookingDate.toLocaleTimeString('ja-JP', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
          }),
          dayOfWeek: DAY_OF_WEEK[bookingDate.getDay()],
          customerType: toHostessVisibleCustomerCategoryEmoji(row.customer_type),
          customerId: row.customer_id || '',
          customerName: row.customer_name || '不明',
          guestCount: row.reserved_guest_count || '-',
          company: row.company_name || undefined,
          isPast,
        };
      });

      const loadedSoFar = (pageParam as number) * PAGE_SIZE;
      const nextPage = loadedSoFar < totalRows ? (pageParam as number) + 1 : undefined;

      return { reservations, totalRows, nextPage };
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.nextPage,
    enabled: !!hostessId,
  });
}
